import React, { useEffect, useRef, useCallback } from 'react';

export default function GlobalParticles() {
  const canvasRef = useRef(null);
  const particles = useRef([]);
  const animRef = useRef(null);
  const lastFrameTime = useRef(0);

  const init = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');

    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    resize();
    window.addEventListener('resize', resize);

    // Keep particles in viewport space so scrolling never affects their motion.
    const totalHeight = window.innerHeight;
    particles.current = Array.from({ length: 320 }, () => ({
      x: Math.random() * canvas.width,
      y: Math.random() * totalHeight,
      r: Math.random() * 2.2 + 0.4,
      dx: (Math.random() - 0.5) * 0.9,
      dy: (Math.random() - 0.5) * 0.7,
      opacity: Math.random() * 0.5 + 0.08,
      color: Math.random() > 0.7 ? '#FFD700' : '#00F0FF',
    }));

    const animate = (now) => {
      const last = lastFrameTime.current || now;
      const delta = Math.min((now - last) / 16.67, 2);
      lastFrameTime.current = now;

      ctx.clearRect(0, 0, canvas.width, canvas.height);
      const vh = canvas.height;
      const totalH = totalHeight;

      particles.current.forEach(p => {
        p.x += p.dx * delta;
        p.y += p.dy * delta;
        p.x += Math.sin((now / 900) + p.y) * 0.015 * delta;
        p.y += Math.cos((now / 1200) + p.x) * 0.01 * delta;

        // Wrap horizontally
        if (p.x < -5) p.x = canvas.width + 5;
        if (p.x > canvas.width + 5) p.x = -5;
        // Wrap vertically in the total virtual space
        if (p.y < 0) p.y = totalH;
        if (p.y > totalH) p.y = 0;

        if (p.y < -10 || p.y > vh + 10) return;

        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fillStyle = p.color;
        ctx.globalAlpha = p.opacity;
        ctx.shadowBlur = 8;
        ctx.shadowColor = p.color;
        ctx.fill();
        ctx.globalAlpha = 1;
        ctx.shadowBlur = 0;
      });
      animRef.current = requestAnimationFrame(animate);
    };
    animRef.current = requestAnimationFrame(animate);

    return () => {
      window.removeEventListener('resize', resize);
      cancelAnimationFrame(animRef.current);
    };
  }, []);

  useEffect(() => {
    const cleanup = init();
    return cleanup;
  }, [init]);

  return (
    <canvas
      ref={canvasRef}
      data-testid="global-particles"
      className="fixed inset-0 pointer-events-none"
      style={{ zIndex: -1, willChange: 'transform' }}
    />
  );
}
